import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 150).bind(this);

  // NEW: cart-level free product dedupe
  #debouncedSyncFreeProducts = debounce(this.#syncFreeProducts, 200).bind(this);
  #isSyncingFreeProducts = false;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    // Some themes dispatch cartAdd; harmless if it never fires
    document.addEventListener(ThemeEvents.cartAdd, this.#handleCartAdd);

    // Ensure free products are at quantity 1 on load
    this.#checkFreeProductsQuantity();

    // NEW: clean duplicates on load
    this.#debouncedSyncFreeProducts();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(ThemeEvents.cartAdd, this.#handleCartAdd);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    // Prevent quantity changes for theme free products / app backorder gifts
    const lineItemRow = this.refs.cartItemRows[line - 1];
    const isLockedGift =
      lineItemRow?.dataset.freeProduct === 'true' || lineItemRow?.dataset.backorderGift === 'true';
    if (isLockedGift && quantity > 0) {
      const quantitySelector = this.refs.quantitySelectors[line - 1];
      const quantityInput = quantitySelector?.querySelector('input');
      if (quantityInput) quantityInput.value = '1';
      return;
    }

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  // async onLineItemRemove(line) {
  //   // If removing a backorder item, ONLY remove free products if this is the LAST backorder in cart.
  //   await this.#removeAssociatedFreeProducts(line);

  //   this.updateQuantity({
  //     line,
  //     quantity: 0,
  //     action: 'clear',
  //   });

  //   const cartItemRowToRemove = this.refs.cartItemRows[line - 1];
  //   if (!cartItemRowToRemove) return;

  //   const remove = () => cartItemRowToRemove.remove();
  //   if (prefersReducedMotion()) return remove();

  //   cartItemRowToRemove.style.setProperty('--row-height', `${cartItemRowToRemove.clientHeight}px`);
  //   cartItemRowToRemove.classList.add('removing');
  //   onAnimationEnd(cartItemRowToRemove, remove);

  //   // After removal settles, clean duplicates / enforce cart-level free products
  //   this.#debouncedSyncFreeProducts();
  // }
    async onLineItemRemove(line) {
  const cartItemRowToRemove = this.refs.cartItemRows[line - 1];
  // App manages this gift — do not allow theme remove
  if (cartItemRowToRemove?.dataset.backorderGift === 'true') return;

  this.updateQuantity({
    line,
    quantity: 0,
    action: 'clear',
  });

  if (!cartItemRowToRemove) return;

  const remove = () => cartItemRowToRemove.remove();

  if (prefersReducedMotion()) {
    remove();
    this.#debouncedSyncFreeProducts?.();
    return;
  }

  cartItemRowToRemove.style.setProperty('--row-height', `${cartItemRowToRemove.clientHeight}px`);
  cartItemRowToRemove.classList.add('removing');
  onAnimationEnd(cartItemRowToRemove, remove);

  this.#debouncedSyncFreeProducts?.();
}

  /**
   * Helper: true if item has Back order property.
   * Shopify keys can vary: "Back order", "Back_order", etc.
   * @param {Record<string, any> | null | undefined} props
   */
  #hasBackOrderProperty(props) {
    if (!props) return false;
    return Object.keys(props).some((key) => {
      const normalized = key.toLowerCase().replace(/[\s_]/g, '');
      // Exclude app free-gift marker (_backorder_gift) — it contains "back"+"order" but is not a backorder parent
      if (normalized === 'backordergift') return false;
      return normalized.includes('back') && normalized.includes('order');
    });
  }

  /**
   * Helper: true if item is a free product.
   * @param {Record<string, any> | null | undefined} props
   */
  #isFreeProduct(props) {
    if (!props) return false;

    // App-managed gift — never treat as theme free product
    if (props['_backorder_gift'] === 'true' || props._backorder_gift === 'true') {
      return false;
    }

    // common formats
    if (props['_Free Product'] !== undefined) return true;
    if (props._Free_Product !== undefined) return true;
    if (props['_Free_Product'] !== undefined) return true;

    // fallback by key name
    return Object.keys(props).some((key) => {
      const normalized = key.toLowerCase().replace(/[\s_]/g, '');
      if (normalized === 'backordergift') return false;
      return normalized.includes('free') && normalized.includes('product');
    });
  }

  /**
   * Collect sections to update across all cart-items components.
   * @returns {Set<string>}
   */
  #getSectionsToUpdate() {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });
    return sectionsToUpdate;
  }

  /**
   * Removes free products when removing the LAST backorder item.
   * (If other backorder items still exist, do nothing.)
   *
   * @param {number} line
   * @returns {Promise<boolean>}
   */
  async #removeAssociatedFreeProducts(line) {
    try {
      const cartResponse = await fetch(Theme.routes.root + 'cart.js');
      const cartData = await cartResponse.json();

      const parentItem = cartData.items?.[line - 1];
      if (!parentItem) return false;

      // Only consider this logic if the removed item is a backorder item
      const isRemovingBackOrder = this.#hasBackOrderProperty(parentItem.properties);
      if (!isRemovingBackOrder) return false;

      // If there are other backorder items in cart, keep the cart-level free products
      const hasOtherBackOrder = cartData.items.some((item, idx) => {
        if (idx === line - 1) return false;
        return this.#hasBackOrderProperty(item.properties);
      });

      if (hasOtherBackOrder) return false;

      // This is the last backorder item => remove all free products
      const freeItems = (cartData.items || []).filter((item) => this.#isFreeProduct(item.properties));
      if (!freeItems.length) return false;

      const sectionsToUpdate = this.#getSectionsToUpdate();
      let lastParsed = null;

      for (const item of freeItems) {
        if (!item.key) continue;

        const body = JSON.stringify({
          id: item.key, // use key to avoid line shifting issues
          quantity: 0,
          sections: Array.from(sectionsToUpdate).join(','),
          sections_url: window.location.pathname,
        });

        const res = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
        const txt = await res.text();
        lastParsed = JSON.parse(txt);

        if (lastParsed?.errors) {
          console.error('Error removing free products:', lastParsed.errors);
          return false;
        }
      }

      if (lastParsed?.sections) {
        Object.keys(lastParsed.sections).forEach((sectionId) => {
          morphSection(sectionId, lastParsed.sections[sectionId]);
        });
      }

      return true;
    } catch (error) {
      console.error('Error removing associated free products:', error);
      return false;
    }
  }

  /**
   * NEW: Cart-level dedupe of free products.
   * Fix: if 3 backorder products add free products 3 times => keep only 1 time.
   */
  async #syncFreeProducts() {
    if (this.#isSyncingFreeProducts) return;
    this.#isSyncingFreeProducts = true;

    try {
      const cartResponse = await fetch(Theme.routes.root + 'cart.js');
      const cartData = await cartResponse.json();

      const items = Array.isArray(cartData.items) ? cartData.items : [];
      if (!items.length) return;

      const hasBackOrder = items.some((item) => this.#hasBackOrderProperty(item.properties));
      const freeItems = items.filter((item) => this.#isFreeProduct(item.properties));

      if (!freeItems.length) return;

      const sectionsToUpdate = this.#getSectionsToUpdate();

      /** @type {{ key: string; quantity: number }[]} */
      const changes = [];

      if (!hasBackOrder) {
        // No backorder => remove free products
        freeItems.forEach((item) => {
          if (item?.key) changes.push({ key: item.key, quantity: 0 });
        });
      } else {
        // Backorder exists => keep only one of each free product variant_id, qty must be 1
        const byVariant = new Map();

        freeItems.forEach((item) => {
          const variantId = String(item.variant_id);
          if (!byVariant.has(variantId)) byVariant.set(variantId, []);
          byVariant.get(variantId).push(item);
        });

        for (const group of byVariant.values()) {
          const keeper = group[0];

          if (keeper?.key && keeper.quantity !== 1) {
            changes.push({ key: keeper.key, quantity: 1 });
          }

          for (let i = 1; i < group.length; i++) {
            const extra = group[i];
            if (extra?.key) changes.push({ key: extra.key, quantity: 0 });
          }
        }
      }

      if (!changes.length) return;

      let lastParsed = null;

      // Apply changes using "id" (line item key) to avoid line shifting problems
      for (const change of changes) {
        const body = JSON.stringify({
          id: change.key,
          quantity: change.quantity,
          sections: Array.from(sectionsToUpdate).join(','),
          sections_url: window.location.pathname,
        });

        const res = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
        const txt = await res.text();
        lastParsed = JSON.parse(txt);

        if (lastParsed?.errors) {
          console.error('Free product sync error:', lastParsed.errors);
          return;
        }
      }

      if (lastParsed?.sections) {
        Object.keys(lastParsed.sections).forEach((sectionId) => {
          morphSection(sectionId, lastParsed.sections[sectionId]);
        });

        // Ensure UI inputs show 1
        this.#checkFreeProductsQuantity();

        // Dispatch update event so other components react correctly
        const newCartRes = await fetch(Theme.routes.root + 'cart.js');
        const newCart = await newCartRes.json();

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCart.item_count,
            source: 'cart-items-component:free-products-sync',
            sections: lastParsed.sections,
          })
        );
      }
    } catch (error) {
      console.error('Error syncing free products:', error);
    } finally {
      this.#isSyncingFreeProducts = false;
    }
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;

    // Prevent quantity changes for theme free products / app backorder gifts
    const lineItemRow = this.refs.cartItemRows[line - 1];
    const isLockedGift =
      lineItemRow?.dataset.freeProduct === 'true' || lineItemRow?.dataset.backorderGift === 'true';
    if (isLockedGift && quantity > 0) {
      this.#enableCartItems();
      const quantitySelector = this.refs.quantitySelectors[line - 1];
      const quantityInput = quantitySelector?.querySelector('input');
      if (quantityInput) quantityInput.value = '1';
      return;
    }

    const { cartTotal } = this.refs;

    const sectionsToUpdate = this.#getSectionsToUpdate();

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        // NEW: after any quantity update, dedupe free products
        this.#debouncedSyncFreeProducts();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles cart add (if theme triggers it).
   * @param {CartAddEvent} _event
   */
  #handleCartAdd = (_event) => {
    this.#debouncedSyncFreeProducts();
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    // Avoid loops while we are applying cart_change calls in sync
    if (this.#isSyncingFreeProducts) return;

    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      this.#debouncedSyncFreeProducts();
      return;
    }

    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }

    // NEW: after any cart update, dedupe free products
    this.#debouncedSyncFreeProducts();
  };

  /**
   * Checks and ensures free products are at quantity 1 on component load.
   */
  #checkFreeProductsQuantity = () => {
    setTimeout(() => {
      const freeProductRows = this.querySelectorAll('[data-free-product="true"]');
      freeProductRows.forEach((row) => {
        const quantitySelector = row.querySelector('quantity-selector');
        if (quantitySelector) {
          const input = quantitySelector.querySelector('input');
          if (input && input.value !== '1') {
            input.value = '1';
          }
        }
      });
    }, 100);
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error('Section id missing');
    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
